import React, { FormEvent, useContext, useMemo, useState } from "react";
import { Button } from "../../Components/ui/buttons/button";
import { Loader2 } from "../../Components/ui/icons";
import { Input } from "../../Components/ui/input";
import { Textarea } from "../../Components/ui/shadcn/textarea";
import { Card, CardContent } from "../../Components/ui/shadcn/card";
import { AuthContext } from "../../context/AuthContext";
import { Navbar } from "../../Components/Navbar";
import logo from "../../Components/Navbar/assets/logo.svg";
import {
  ContactRequestInput,
  FaleConoscoServiceError,
  PlatformEvaluationInput,
} from "../../global/types/faleConosco";
import {
  createContactRequest,
  createPlatformEvaluation,
} from "../../services/faleConosco";

type EvaluationRatingKey =
  | "generalExperienceRating"
  | "informationRelevanceRating"
  | "crossingEaseRating"
  | "reportClarityRating"
  | "planningSupportRating"
  | "institutionalUsefulnessRating";

type EvaluationFormState = {
  fullName: string;
  email: string;
  lgpdConsent: boolean;
} & Record<EvaluationRatingKey, number | null>;

const emptyContact: ContactRequestInput = {
  fullName: "",
  email: "",
  message: "",
  lgpdConsent: false,
};

const emptyEvaluation: EvaluationFormState = {
  fullName: "",
  email: "",
  lgpdConsent: false,
  generalExperienceRating: null,
  informationRelevanceRating: null,
  crossingEaseRating: null,
  reportClarityRating: null,
  planningSupportRating: null,
  institutionalUsefulnessRating: null,
};

const getInitialContact = (
  fullName: string,
  email: string
): ContactRequestInput => ({
  ...emptyContact,
  fullName,
  email,
});

const getInitialEvaluation = (
  fullName: string,
  email: string
): EvaluationFormState => ({
  ...emptyEvaluation,
  fullName,
  email,
});

const evaluationQuestions: Array<{ key: EvaluationRatingKey; label: string }> =
  [
    {
      key: "generalExperienceRating",
      label:
        "Quão satisfeito você está com a experiência geral de uso da plataforma?",
    },
    {
      key: "informationRelevanceRating",
      label:
        "Quão relevantes são as informações disponibilizadas para a análise territorial e energética?",
    },
    {
      key: "crossingEaseRating",
      label:
        "Quão fácil é cruzar informações sobre potencial energético, infraestrutura e condicionantes territoriais na plataforma?",
    },
    {
      key: "reportClarityRating",
      label:
        "Quão satisfeito você está com a clareza e a utilidade dos relatórios gerados?",
    },
    {
      key: "planningSupportRating",
      label:
        "Quão satisfeito você está com o potencial da plataforma para apoiar o planejamento, a tomada de decisão e a realização de estudos técnicos?",
    },
    {
      key: "institutionalUsefulnessRating",
      label:
        "Quão útil você considera a Plataforma Energética de Pernambuco para as atividades da sua instituição?",
    },
  ];

const fieldClassName = "flex flex-col gap-1 text-sm font-medium text-[#0c2856]";
const cardClassName = "w-full rounded-md border-0 bg-white shadow-sm";
const lpHomeUrl = "https://plataformaenergetica.pe.gov.br/";
const lpAboutUrl = "https://plataformaenergetica.pe.gov.br/sobre.html";

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof FaleConoscoServiceError) {
    return error.message;
  }
  return fallback;
};

const hasAllRatings = (
  evaluation: EvaluationFormState
): evaluation is EvaluationFormState & Record<EvaluationRatingKey, number> =>
  evaluationQuestions.every(({ key }) => evaluation[key] !== null);

const toEvaluationInput = (
  evaluation: EvaluationFormState & Record<EvaluationRatingKey, number>
): PlatformEvaluationInput => ({
  fullName: evaluation.fullName.trim() || null,
  email: evaluation.email.trim().toLowerCase() || null,
  generalExperienceRating: evaluation.generalExperienceRating,
  informationRelevanceRating: evaluation.informationRelevanceRating,
  crossingEaseRating: evaluation.crossingEaseRating,
  reportClarityRating: evaluation.reportClarityRating,
  planningSupportRating: evaluation.planningSupportRating,
  institutionalUsefulnessRating: evaluation.institutionalUsefulnessRating,
  lgpdConsent: evaluation.lgpdConsent,
});

const RatingScale: React.FC<{
  name: EvaluationRatingKey;
  value: number | null;
  onChange: (value: number) => void;
}> = ({ name, value, onChange }) => (
  <div className="mt-3">
    <div className="relative mx-2 flex h-8 items-center justify-between">
      <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-gray-400" />
      {[1, 2, 3, 4, 5].map((rating) => {
        const selected = value === rating;
        return (
          <label
            key={rating}
            className="relative z-10 flex h-8 w-8 cursor-pointer items-center justify-center"
          >
            <span className="sr-only">Nota {rating} de 5</span>
            <input
              type="radio"
              name={name}
              value={rating}
              checked={selected}
              onChange={() => onChange(rating)}
              className="sr-only"
            />
            <span
              className={[
                "relative flex h-4 w-4 items-center justify-center rounded-full border bg-white transition",
                selected
                  ? "border-2 border-[#0c2856] shadow-[0_0_0_3px_rgba(12,40,86,0.16)]"
                  : "border-gray-500",
              ].join(" ")}
            >
              {selected && (
                <span className="block h-2 w-2 rounded-full bg-[#0c2856]" />
              )}
            </span>
          </label>
        );
      })}
    </div>
    <div className="flex justify-between text-xs text-gray-700">
      <span>Pouco</span>
      <span>Neutro</span>
      <span>Muito</span>
    </div>
  </div>
);

const PublicHeader: React.FC = () => (
  <header className="sticky top-0 z-20 grid h-16 grid-cols-[180px_1fr_180px] items-center bg-white px-20 shadow-[0_2px_4px_rgba(8,13,26,0.15)] max-[920px]:grid-cols-[1fr_auto] max-[920px]:px-7">
    <a
      className="inline-flex w-max items-center"
      href={lpHomeUrl}
      aria-label="Plataforma Energética de Pernambuco"
    >
      <img
        className="h-10 w-36 object-contain"
        src={logo}
        alt="Plataforma Energética de Pernambuco"
      />
    </a>

    <nav
      className="flex h-full justify-center gap-4 max-[920px]:hidden"
      aria-label="Navegação principal"
    >
      <a
        className="inline-flex h-full items-center justify-center px-6 text-[13px] font-semibold uppercase leading-4 tracking-[0.08em] text-[#858890] hover:text-[#0c2856]"
        href={lpHomeUrl}
      >
        Início
      </a>
      <a
        className="inline-flex h-full items-center justify-center px-6 text-[13px] font-semibold uppercase leading-4 tracking-[0.08em] text-[#858890] hover:text-[#0c2856]"
        href={lpAboutUrl}
      >
        Sobre
      </a>
      <a
        className="relative inline-flex h-full items-center justify-center px-6 text-[13px] font-semibold uppercase leading-4 tracking-[0.08em] text-[#0c2856] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[#0c2856]"
        href="/fale-conosco"
        aria-current="page"
      >
        Fale Conosco
      </a>
    </nav>

    <a
      className="justify-self-end rounded-lg bg-[#0c2856] px-5 py-2.5 text-sm font-medium leading-5 text-white hover:bg-[#091831ff]"
      href="/"
    >
      Acessar
    </a>
  </header>
);

export const FaleConosco: React.FC = () => {
  const { signed, user } = useContext(AuthContext);
  const showLoggedHeader = signed && Boolean(user);
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [isEvaluationSubmitting, setIsEvaluationSubmitting] = useState(false);
  const defaultFullName = useMemo(() => {
    if (!user) return "";
    return (
      [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username
    );
  }, [user]);
  const defaultEmail = user?.email || "";

  const [contact, setContact] = useState<ContactRequestInput>(() =>
    getInitialContact(defaultFullName, defaultEmail)
  );
  const [evaluation, setEvaluation] = useState<EvaluationFormState>(() =>
    getInitialEvaluation(defaultFullName, defaultEmail)
  );
  const [contactMessage, setContactMessage] = useState<string | null>(null);
  const [contactError, setContactError] = useState<string | null>(null);
  const [evaluationMessage, setEvaluationMessage] = useState<string | null>(
    null
  );
  const [evaluationError, setEvaluationError] = useState<string | null>(null);

  const validateContact = () => {
    if (!contact.fullName.trim()) return "Informe seu nome.";
    if (!contact.email.trim()) return "Informe seu e-mail.";
    if (!contact.message.trim()) return "Informe sua mensagem.";
    if (!contact.lgpdConsent) return "Confirme o consentimento LGPD.";
    return null;
  };

  const handleContactSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isContactSubmitting) return;

    setContactMessage(null);
    setContactError(null);
    const validationError = validateContact();
    if (validationError) {
      setContactError(validationError);
      return;
    }

    setIsContactSubmitting(true);
    try {
      await createContactRequest({
        ...contact,
        email: contact.email.trim().toLowerCase(),
        fullName: contact.fullName.trim(),
        message: contact.message.trim(),
      });
      setContactMessage("Mensagem recebida.");
      setContact(getInitialContact(defaultFullName, defaultEmail));
    } catch (error) {
      setContactError(
        getErrorMessage(error, "Não foi possível enviar a mensagem.")
      );
    } finally {
      setIsContactSubmitting(false);
    }
  };

  const handleEvaluationSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isEvaluationSubmitting) return;

    setEvaluationMessage(null);
    setEvaluationError(null);
    if (!hasAllRatings(evaluation)) {
      setEvaluationError("Responda todas as perguntas da avaliação.");
      return;
    }
    if (!evaluation.lgpdConsent) {
      setEvaluationError("Confirme o consentimento LGPD.");
      return;
    }

    setIsEvaluationSubmitting(true);
    try {
      await createPlatformEvaluation(toEvaluationInput(evaluation));
      setEvaluationMessage("Avaliação recebida.");
      setEvaluation(getInitialEvaluation(defaultFullName, defaultEmail));
    } catch (error) {
      setEvaluationError(
        getErrorMessage(error, "Não foi possível enviar a avaliação.")
      );
    } finally {
      setIsEvaluationSubmitting(false);
    }
  };

  return (
    <>
      {showLoggedHeader ? <Navbar /> : <PublicHeader />}
      <main
        className={[
          "min-h-screen bg-[#f7f8fb] px-4 text-gray-900 sm:px-8",
          showLoggedHeader ? "pb-10 pt-24" : "py-10",
        ].join(" ")}
      >
        <div className="mx-auto w-full max-w-6xl">
          <h1 className="text-3xl font-semibold tracking-normal text-gray-950">
            Fale Conosco
          </h1>

          <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
            <section>
              <h2 className="text-lg font-semibold text-gray-950">
                Entre em contato
              </h2>
              <p className="mt-2 max-w-xs text-sm leading-5 text-gray-600">
                Preencha o formulário ao lado que a nossa equipe responderá
                você.
              </p>
            </section>

            <Card className={cardClassName}>
              <CardContent className="p-6">
                <form
                  className="flex flex-col gap-4"
                  onSubmit={handleContactSubmit}
                >
                  {contactMessage && (
                    <div
                      role="status"
                      className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-800"
                    >
                      {contactMessage}
                    </div>
                  )}
                  {contactError && (
                    <div
                      role="alert"
                      className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                    >
                      {contactError}
                    </div>
                  )}

                  <label className={fieldClassName}>
                    Nome
                    <Input
                      value={contact.fullName}
                      maxLength={255}
                      placeholder="Digite seu nome..."
                      onChange={(event) =>
                        setContact({ ...contact, fullName: event.target.value })
                      }
                    />
                  </label>
                  <label className={fieldClassName}>
                    E-mail
                    <Input
                      type="email"
                      value={contact.email}
                      maxLength={320}
                      placeholder="exemplo@gmail.com"
                      onChange={(event) =>
                        setContact({ ...contact, email: event.target.value })
                      }
                    />
                  </label>
                  <label className={fieldClassName}>
                    Deixe suas dúvidas, comentários ou sugestões
                    <Textarea
                      value={contact.message}
                      maxLength={4000}
                      placeholder="Envie seus questionamentos, avaliações ou palpites."
                      onChange={(event) =>
                        setContact({ ...contact, message: event.target.value })
                      }
                    />
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={contact.lgpdConsent}
                      onChange={(event) =>
                        setContact({
                          ...contact,
                          lgpdConsent: event.target.checked,
                        })
                      }
                    />
                    Autorizo o tratamento dos dados enviados para atendimento
                    desta solicitação.
                  </label>
                  <Button
                    type="submit"
                    disabled={isContactSubmitting}
                    className="ml-auto bg-[#0c2856] px-6 text-white hover:bg-[#091831ff]"
                  >
                    {isContactSubmitting && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {isContactSubmitting ? "Enviando..." : "Enviar mensagem"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <section>
              <h2 className="text-lg font-semibold text-gray-950">
                Avalie nossa plataforma
              </h2>
              <p className="mt-2 max-w-xs text-sm leading-5 text-gray-600">
                Seu feedback nos ajuda a identificar oportunidades de melhoria e
                evolução.
              </p>
            </section>

            <Card className={cardClassName}>
              <CardContent className="p-6">
                <form
                  className="flex flex-col gap-8"
                  onSubmit={handleEvaluationSubmit}
                >
                  {evaluationMessage && (
                    <div
                      role="status"
                      className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-800"
                    >
                      {evaluationMessage}
                    </div>
                  )}
                  {evaluationError && (
                    <div
                      role="alert"
                      className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                    >
                      {evaluationError}
                    </div>
                  )}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className={fieldClassName}>
                      Nome (opcional)
                      <Input
                        value={evaluation.fullName}
                        maxLength={255}
                        placeholder="Digite seu nome..."
                        onChange={(event) =>
                          setEvaluation({
                            ...evaluation,
                            fullName: event.target.value,
                          })
                        }
                      />
                    </label>
                    <label className={fieldClassName}>
                      E-mail (opcional)
                      <Input
                        type="email"
                        value={evaluation.email}
                        maxLength={320}
                        placeholder="exemplo@gmail.com"
                        onChange={(event) =>
                          setEvaluation({
                            ...evaluation,
                            email: event.target.value,
                          })
                        }
                      />
                    </label>
                  </div>

                  {evaluationQuestions.map((question) => (
                    <fieldset key={question.key}>
                      <legend className="text-base font-semibold leading-6 text-[#0c2856]">
                        {question.label}
                      </legend>
                      <RatingScale
                        name={question.key}
                        value={evaluation[question.key]}
                        onChange={(value) =>
                          setEvaluation({
                            ...evaluation,
                            [question.key]: value,
                          })
                        }
                      />
                    </fieldset>
                  ))}

                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={evaluation.lgpdConsent}
                      onChange={(event) =>
                        setEvaluation({
                          ...evaluation,
                          lgpdConsent: event.target.checked,
                        })
                      }
                    />
                    Autorizo o tratamento dos dados enviados para atendimento
                    desta solicitação.
                  </label>

                  <Button
                    type="submit"
                    disabled={isEvaluationSubmitting}
                    className="ml-auto bg-[#0c2856] px-6 text-white hover:bg-[#091831ff]"
                  >
                    {isEvaluationSubmitting && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {isEvaluationSubmitting ? "Enviando..." : "Enviar avaliação"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </>
  );
};
